import React, { useCallback, useEffect, useState, useMemo } from 'react'

import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'

import { useStatsigExperiment } from './useStatsigExperiment'

/**
 * Generic component for Statsig experiments that only renders when needed
 */
interface StatsigExperimentProps {
  experimentKey: STATSIG_EXPERIMENTS
  onRender: (bucket: string | null) => void
}

const StatsigExperiment: React.FC<StatsigExperimentProps> = ({ experimentKey, onRender }) => {
  const { experimentBucket } = useStatsigExperiment(experimentKey)

  useEffect(() => {
    onRender(experimentBucket)
    return () => {
      onRender(null)
    }
  }, [experimentBucket, onRender])

  return null
}

/**
 * Hook for conditionally rendering a Statsig experiment
 * @param experimentKey - The statsig experiment key
 * @param shouldRenderCondition - Function that determines if the experiment should be rendered
 * @returns An object containing the experiment bucket and component
 */
export function useConditionalStatsigExperiment(
  experimentKey: STATSIG_EXPERIMENTS,
  shouldRenderCondition: () => boolean,
): { bucket: string | null; component: React.ReactNode } {
  const [experimentBucket, setExperimentBucket] = useState<string | null>(null)

  const handleExperimentResult = useCallback((bucket: string | null) => {
    setExperimentBucket(bucket)
  }, [])

  const shouldRender = useMemo(() => shouldRenderCondition(), [shouldRenderCondition])

  const experimentComponent = useMemo(() => {
    return shouldRender ? <StatsigExperiment experimentKey={experimentKey} onRender={handleExperimentResult} /> : null
  }, [shouldRender, experimentKey, handleExperimentResult])

  return { bucket: experimentBucket, component: experimentComponent }
}
